import { PDFName, PDFString, PDFHexString } from 'pdf-lib';

const METADATA_KEY = 'ScrivarOverlays';

/**
 * Serialize overlay state (edits + annotations) into a JSON string.
 * Groups by page, only includes pages with overlays.
 */
export function serializeOverlays(edits, annotations, pageCount) {
  const pages = {};

  // Group edits by page. Skip deleted DETECTED edits — the save whites out
  // their original glyphs in the content stream, so there is nothing left to
  // restore; keeping them in the sidecar resurrects the deletion as a
  // tombstone on reopen. Deleted restored/user-added edits keep their
  // existing round-trip semantics (their bakedBounds whiteout needs the
  // tombstone to stay covered across re-saves).
  for (const edit of edits) {
    if (edit.deleted && edit.source === 'detected') continue;
    const key = String(edit.pageNum);
    if (!pages[key]) pages[key] = { edits: [], annotations: [] };
    // Deep clone — don't mutate originals
    const clone = { ...edit, styles: edit.styles ? { ...edit.styles } : undefined };
    // Deep clone segments for rich text edits
    if (edit.segments) {
      clone.segments = edit.segments.map((seg) => ({ text: seg.text, styles: { ...seg.styles } }));
    }
    delete clone.editing; // transient UI state
    delete clone.apCarried; // session state — recomputed from the bytes on reopen
    pages[key].edits.push(clone);
  }

  // Group annotations by page. Skip tombstoned deletions — once the save
  // has whited out their baked pixels there's nothing left to restore, and
  // keeping them in the sidecar zombies them back on reopen.
  for (const ann of annotations) {
    if (ann.deleted) continue;
    const key = String(ann.pageNum);
    if (!pages[key]) pages[key] = { edits: [], annotations: [] };
    const clone = { ...ann };
    // Drop transient whiteout-bookkeeping flags — the next restore computes
    // a fresh bakedBounds from the saved geometry, and `restored`/`modified`
    // describe session state, not on-disk truth.
    delete clone.restored;
    delete clone.modified;
    delete clone.bakedBounds;
    delete clone.apCarried; // session state — recomputed from the bytes on reopen
    if (clone.points) clone.points = clone.points.map((p) => ({ ...p }));
    pages[key].annotations.push(clone);
  }

  // Only include pages with content
  const filteredPages = {};
  for (const [key, val] of Object.entries(pages)) {
    if (val.edits.length > 0 || val.annotations.length > 0) {
      filteredPages[key] = val;
    }
  }

  const data = {
    version: 1,
    app: 'Scrivar',
    savedAt: new Date().toISOString(),
    pages: filteredPages,
  };

  return JSON.stringify(data);
}

/**
 * Deserialize JSON string back to flat overlay arrays.
 * Reconstructs pageNum from page keys. Marks flattened edits as locked.
 */
export function deserializeOverlays(jsonString) {
  const data = JSON.parse(jsonString);

  if (data.version !== 1 || data.app !== 'Scrivar') {
    throw new Error('Unrecognized overlay metadata format');
  }

  const edits = [];
  const annotations = [];

  for (const [pageKey, page] of Object.entries(data.pages)) {
    const pageNum = parseInt(pageKey, 10);

    for (const edit of (page.edits || [])) {
      const restored = { ...edit, pageNum };
      if (edit.content === '__FLATTENED__') {
        restored.flattened = true;
        restored.locked = true;
      }
      edits.push(restored);
    }

    for (const ann of (page.annotations || [])) {
      annotations.push({ ...ann, pageNum });
    }
  }

  return { edits, annotations, version: data.version, savedAt: data.savedAt };
}

/**
 * Tag sidecar entries whose visual was carried by a real /AP annot in the
 * opened bytes (per stripScrivarAnnotsToBytes' `apCarriedIds`). Those entries
 * have no pixels baked in the content stream, so restore must not fabricate
 * `bakedBounds` for them — MaskLayer and the save-pipeline whiteout would
 * paint white over raw page pixels (invisible on white documents, a glaring
 * white box on scans). Entries NOT in the set keep legacy semantics: their
 * pixels are (or may be) genuinely baked, so masks + whiteouts still apply.
 *
 * Pure — returns a new sidecar object, inputs untouched.
 */
export function tagApCarriedOverlays(sidecar, apCarriedIds) {
  if (!sidecar || !apCarriedIds || apCarriedIds.size === 0) return sidecar;
  const tag = (item) => (apCarriedIds.has(item.id) ? { ...item, apCarried: true } : item);
  return {
    ...sidecar,
    edits: (sidecar.edits || []).map(tag),
    annotations: (sidecar.annotations || []).map(tag),
  };
}

/**
 * Embed serialized overlay data into a pdf-lib PDFDocument.
 * Uses the Info dictionary with a custom key for reliable round-tripping.
 */
export async function embedOverlayMetadata(pdfDoc, edits, annotations, pageCount) {
  const json = serializeOverlays(edits, annotations, pageCount);
  // Base64 encode (handles Unicode)
  const base64 = btoa(unescape(encodeURIComponent(json)));

  // Store in Info dictionary as a custom key
  const infoDict = pdfDoc.context.lookup(pdfDoc.context.trailerInfo.Info);
  if (infoDict) {
    infoDict.set(PDFName.of(METADATA_KEY), PDFString.of(base64));
  } else {
    // Create info dict entry via catalog approach
    const infoDictRef = pdfDoc.context.trailerInfo.Info;
    if (infoDictRef) {
      const dict = pdfDoc.context.lookup(infoDictRef);
      if (dict) dict.set(PDFName.of(METADATA_KEY), PDFString.of(base64));
    }
  }
}

/**
 * Extract overlay data from a pdf-lib PDFDocument.
 * Returns deserialized overlays or null if none found.
 */
export async function extractOverlayMetadata(pdfDoc) {
  try {
    const infoDictRef = pdfDoc.context.trailerInfo.Info;
    if (!infoDictRef) return null;

    const infoDict = pdfDoc.context.lookup(infoDictRef);
    if (!infoDict) return null;

    const val = infoDict.get(PDFName.of(METADATA_KEY));
    if (!val) return null;

    // PDFHexString or PDFString — extract the text value.
    let base64;
    if (val instanceof PDFString) {
      // Read `.value` directly — pdf-lib's `.decodeText()` calls
      // `pdfDocEncodingDecode` which does `String.fromCodePoint.apply(String,
      // codePoints)`. That argument-splat blows the JS argument stack on
      // payloads over ~100 KB, so an overlay sidecar with embedded images
      // (base64 easily > 1 MB) stack-overflows on reopen. Our stored value is
      // always ASCII base64, so `.value` — populated by PDFObjectParser byte-
      // by-byte — is already the string we want, no decode needed.
      base64 = val.value;
    } else if (val instanceof PDFHexString) {
      // Legacy format: PDFHexString with UTF-16 encoded base64. decodeText uses
      // recursive utf16Decode which stack-overflows on large payloads. Decode
      // manually via the raw hex string without recursion.
      base64 = decodeHexStringToAscii(val.toString());
    } else {
      // Unknown — toString and strip parens/angle brackets as a last resort.
      base64 = val.toString().replace(/^[(<]|[>)]$/g, '');
    }

    const json = decodeURIComponent(escape(atob(base64)));
    return deserializeOverlays(json);
  } catch (err) {
    console.warn('Failed to extract Scrivar overlay metadata:', err);
    return null;
  }
}

/**
 * Remove overlay metadata from a pdf-lib PDFDocument.
 * Used when permanently flattening.
 */
export async function stripOverlayMetadata(pdfDoc) {
  try {
    const infoDictRef = pdfDoc.context.trailerInfo.Info;
    if (!infoDictRef) return;

    const infoDict = pdfDoc.context.lookup(infoDictRef);
    if (!infoDict) return;

    infoDict.delete(PDFName.of(METADATA_KEY));
  } catch (err) {
    console.warn('Failed to strip Scrivar overlay metadata:', err);
  }
}

/**
 * Decode a PDFHexString's toString() form ("<...>" or similar) back to ASCII.
 * pdf-lib's built-in decodeText uses recursive UTF-16 decode which overflows on
 * large payloads. Since our sidecar stores ASCII-only base64, we can walk the
 * hex bytes and take every other one (UTF-16 BE puts the ASCII char in the low
 * byte with a 0x00 high byte). Iterative — no stack recursion.
 */
function decodeHexStringToAscii(raw) {
  // raw is like "<0066...>"; strip delimiters.
  const hex = raw.replace(/^[<]|[>]$/g, '').replace(/\s+/g, '');
  // UTF-16 BE: every char is 2 hex bytes; ASCII chars have high byte 0x00, low byte = char code.
  // So pairs look like "00XX" where XX is the ASCII code.
  const out = [];
  for (let i = 0; i < hex.length; i += 4) {
    const low = hex.slice(i + 2, i + 4);
    if (low.length !== 2) break;
    out.push(String.fromCharCode(parseInt(low, 16)));
  }
  return out.join('');
}
